import MarketplaceStore from '../../infrastructure/store/marketplace-store';
import {DocumentMetadataResponse} from '../../entity/resource/document-metadata-response';
import {MarketplaceSearchDto} from '../../entity/dto/marketplace-search-dto';
import {DocumentMetadataRequestDto} from '../../entity/dto/document-metadata-request-dto';
import {toast} from 'react-toastify';
import {FilterMetadataRequestDto} from '../../entity/dto/filter-metadata-request-dto';
import {FilterMetadataResponse} from '../../entity/resource/filter-metadata-response';
import {MarketplaceRecordRequestDto} from '../../entity/dto/marketplace-record-request-dto';
import {MarketplaceRecordResponse} from '../../entity/resource/marketplace-record-response';
import {MarketplacePurchaseRecordResponse} from '../../entity/resource/marketplace-purchase-record';
import {MetadataType} from '../../entity/enums/metadata-type';

export default class MarketplaceService {
  constructor(private marketplaceStore: MarketplaceStore) {}

  getDocumentMetadata = async (metadataType: MetadataType, stateID: string, countyID: string, indexType?: string, dateRange?: string): Promise<DocumentMetadataResponse | undefined>  => {
    const payload: DocumentMetadataRequestDto = {
      metadataType: metadataType,
      stateID: stateID,
      countyID: countyID,
      ...(indexType && { indexType }),
      ...(dateRange && { dateRange })
    };
    const response = await this.marketplaceStore.getDocumentMetadata(payload);

    if (typeof response === 'string') {
      toast.error(response);
      return;
    } else
      return response;
  }

  filterMetaData = async (payload: FilterMetadataRequestDto): Promise<FilterMetadataResponse[] | undefined> => {
    const response = await this.marketplaceStore.filterMetaData(payload);

    if (typeof response === 'string') {
      toast.error(response);
      return;
    } else
      return response;
  }

  createMarketplaceRecord = async (organizationID: string, payload: MarketplaceRecordRequestDto): Promise<MarketplaceRecordResponse | undefined | string> => {
    return await this.marketplaceStore.createMarketplaceRecord(organizationID, payload);
  }

  downloadMarketplaceDocument = async (organizationId: string, batchID: string): Promise<string | undefined> => {
    try {
      const response = await this.marketplaceStore.downloadMarketplaceDocument(organizationId, batchID);

      if (typeof response === 'string') {
        toast.error(response);
        return undefined;
      }

      if (!response.success) {
        toast.error(response.message || 'Failed to generate download URL');
        return undefined;
      }
      return response.downloadUrl;

    } catch (error) {
      toast.error('Failed to get download URL. Please try again.');
      return undefined;
    }
  }

  showTimeoutMessage = (): void => {
    toast.info('Processing your download. Please refresh to check the progress.');
  }

  showImportTimeoutMessage = (): void => {
    toast.info('Processing your import. Please refresh to check the progress.');
  }

  getMarketplaceSearchDto = (): MarketplaceSearchDto => {
    return {
      selectedState: '',
      selectedCounty: '',
      selectedBookType: '',
      selectedIndexType: '',
      dateRange: '',
      selectedIndexBook: '',
      selectedRecordedBook: '',
      pageFrom: '',
      pageTo: ''
    };
  }

  downloadFile = async (downloadUrl: string, filename: string): Promise<void> => {
    if (!downloadUrl || !downloadUrl.startsWith('http'))
      throw new Error('Invalid download URL provided');

    const safeFilename = filename.toLowerCase().endsWith('.pdf') ? filename : `${filename}.pdf`;
    const response = await fetch(downloadUrl);
    if (!response.ok)
      throw new Error(`Failed to fetch file: ${response.statusText}`);

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = safeFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Document downloaded successfully!');
  }

  getMarketplacePurchaseRecords = async (organizationId: string): Promise<MarketplacePurchaseRecordResponse[] | undefined> => {
    const response = await this.marketplaceStore.getMarketplacePurchaseRecords(organizationId);

    if (typeof response === 'string') {
      toast.error(response);
      return;
    } else
      return response;
  }
}
